import { PlayerAPI, PlayerEvent } from 'bitmovin-player';
import classNames from 'classnames';
import { log } from '@intigral/utils';
import { ForwardedRef, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { BitmovinPlayerProps } from '../types/iBasePlayer';
import { createBitmovinPlayer, destroyPlayer, initializePlayerUi, setRef } from '../utils/BasePlayerUtils';
import { isPlayerAlive } from '../../../helpers/playerGuards';
import styles from '../styles/player.module.sass';

export const BaseBitmovinPlayer = forwardRef(function BitmovinPlayer(
    {
        config,
        source,
        className,
        playerRef: playerRefProp,
        customUi,
        onUIManagerReady,
        playerWrapper,
        onBeforeSourceLoaded,
    }: BitmovinPlayerProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
) {
    const rootContainerElementRef = useRef<HTMLDivElement | null>(null);
    const rootContainerElementRefHandler = (rootContainerElement: HTMLDivElement | null) => {
        setRef(rootContainerElementRef, rootContainerElement);

        if (forwardedRef) {
            setRef(forwardedRef, rootContainerElement);
        }
    };

    const isInitialSourceEmptyRef = useRef<boolean>(!source);
    const isSourceChangedAtLeastOnce = useRef<boolean>(false);

    const [player, setPlayer] = useState<PlayerAPI | undefined>();
    const latestPlayerRef = useRef<PlayerAPI | undefined>(undefined);

    // When the player instance is owned by the parent (`playerWrapper`), its lifecycle must follow
    // that instance only. `config` / `customUi` / `onUIManagerReady` are then just read at init
    // time; a new identity of those props must not destroy a live player and re-initialize the UI
    // against the very same (now destroyed) instance — that is what produced the
    // `player.subtitles` / `player.on` API_NOT_AVAILABLE errors.
    const ownsPlayer = !playerWrapper;
    const latestPropsRef = useRef({ config, customUi, onUIManagerReady });
    latestPropsRef.current = { config, customUi, onUIManagerReady };

    const proxyPlayerRef = useCallback(
        (player: PlayerAPI) => {
            if (!playerRefProp) {
                return;
            }

            setRef(playerRefProp, player);
        },
        // Only the first `playerRef` is relevant and should be used to avoid
        // unnecessary ref callback invocations (in case the `playerRef` is a ref function).
        []
    );

    // Initialize the player on mount.
    useEffect(() => {
        const rootContainerElement = rootContainerElementRef.current;

        if (!rootContainerElement) {
            return;
        }

        const {
            config: currentConfig,
            customUi: currentCustomUi,
            onUIManagerReady: currentOnUIManagerReady,
        } = latestPropsRef.current;

        const configWithEventHandlersAdded = {
            ...currentConfig,
            events: {
                ...currentConfig?.events,
                [PlayerEvent.Error]: (error) => {
                    log.error('Player error:', error);
                },
            },
        };

        // We create elements manually to workaround the React strict mode.
        // In the strict mode the mount hook is invoked twice. Since the destroy method is async
        // the next mount hook is invoked before the previous destroy method is finished and the new player instance
        // messes up the old one. This workaround ensures that each player instance has its own container and video elements.
        // This should be improved in the future if possible.
        const wrapper = playerWrapper ?? createBitmovinPlayer(configWithEventHandlersAdded);

        // Never build a UI on top of an instance that has already been destroyed (e.g. a stale
        // wrapper handed down after its player was torn down elsewhere).
        if (!isPlayerAlive(wrapper.initializedPlayer)) {
            log.warn('[BaseBitmovinPlayer] skipping initialization: player instance is already destroyed');
            return;
        }

        rootContainerElement.appendChild(wrapper.createdPlayerContainerElement);

        const uiManager = initializePlayerUi(wrapper.initializedPlayer, configWithEventHandlersAdded, currentCustomUi);

        if (currentOnUIManagerReady && uiManager) {
            currentOnUIManagerReady(uiManager);
        }

        latestPlayerRef.current = wrapper.initializedPlayer;

        proxyPlayerRef(wrapper.initializedPlayer);
        setPlayer(wrapper.initializedPlayer);

        return () => {
            destroyPlayer(
                wrapper.initializedPlayer,
                rootContainerElement,
                wrapper.createdPlayerContainerElement,
                uiManager
            );
        };
        // Fixed-length dependency list: with an external `playerWrapper` only the instance matters;
        // when this component owns the player, `config` / `customUi` / `onUIManagerReady` drive it.
    }, [
        playerWrapper,
        ownsPlayer ? config : null,
        ownsPlayer ? customUi : null,
        ownsPlayer ? onUIManagerReady : null,
        proxyPlayerRef,
    ]);

    // Load or reload the source.
    useEffect(() => {
        if (
            !player ||
            // Skip if the player is not the latest one, this happens in case of HMR.
            player !== latestPlayerRef.current ||
            // Skip if the instance has been destroyed in the meantime (load/unload would throw 1001).
            !isPlayerAlive(player)
        ) {
            return;
        }

        if (source) {
            if (onBeforeSourceLoaded) {
                onBeforeSourceLoaded(source);
            }

            player.load(source).catch((error) => {
                // Catching the error here to avoid unhandled promise rejection errors.
                // The error is also emitted in the player instance, so users can still listen to it there.
                log.error('Loading the source failed:', error);
            });
            setRef(isSourceChangedAtLeastOnce, true);
        } else {
            // Skip unloading the player if the source is empty on mount.
            // This is useful in case users want to use the player instance ref to load the source manually,
            // so this ensures that we do not unload the imperatively loaded source.
            // TODO do we need it?
            //
            // Apart from that, this check ensures that `player.unload` is not called unnecessarily on mount if the source is empty.
            // TODO do we actually care?
            const shouldSkipUnload = isInitialSourceEmptyRef.current && !isSourceChangedAtLeastOnce.current;

            if (!shouldSkipUnload) {
                player.unload();
                setRef(isSourceChangedAtLeastOnce, true);
            }
        }
    }, [source, player]);

    return <div className={classNames(className, styles.baseplayer)} ref={rootContainerElementRefHandler} />;
    // `id` might be needed for some custom UI implementations, so we keep it for now
    // return <div id="player_main-player" className={classNames(className, styles.baseplayer)} ref={rootContainerElementRefHandler} />;
});
